import type { FC } from 'react';
import { Link } from 'react-router-dom';

import { CONTACT, LEGAL_ENTITY, SITE } from '@/constants/site.constants';
import { ROUTES } from '@/routes/route.config';

interface FooterLink {
  label: string;
  href: string;
}

interface SiteFooterProps {
  productLinks: FooterLink[];
  year: number;
}

const SiteFooter: FC<SiteFooterProps> = ({ productLinks, year }) => (
  <footer className="bg-[var(--color-sidebar)] text-white">
    <div className="mx-auto grid w-full max-w-6xl gap-10 px-5 py-14 md:grid-cols-[1.4fr_1fr_1fr] md:px-8">
      <div className="flex flex-col gap-4">
        <img src="/shoutt-wordmark-white.svg" alt="Shoutt" className="h-7 w-auto self-start" />
        <p className="max-w-sm font-body text-sm leading-relaxed text-white/60">{SITE.tagline}</p>
        <a
          href={`mailto:${CONTACT.email}`}
          className="font-body text-sm font-medium text-white/80 transition-colors hover:text-white"
        >
          {CONTACT.email}
        </a>
      </div>

      <nav aria-label="Product">
        <h2 className="font-display text-xs font-bold tracking-[0.14em] text-white/40 uppercase">
          Product
        </h2>
        <ul className="mt-4 flex flex-col gap-3">
          {productLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="font-body text-sm text-white/70 transition-colors hover:text-white"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <nav aria-label="Legal">
        <h2 className="font-display text-xs font-bold tracking-[0.14em] text-white/40 uppercase">
          Legal
        </h2>
        <ul className="mt-4 flex flex-col gap-3">
          <li>
            <Link
              to={ROUTES.terms}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              Terms &amp; Conditions
            </Link>
          </li>
          <li>
            <Link
              to={ROUTES.rentalTerms}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              Rentals Terms
            </Link>
          </li>
          <li>
            <Link
              to={ROUTES.privacy}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              Privacy Policy
            </Link>
          </li>
          {/* Play requires the deletion path to be prominently featured and easily
              discoverable — a footer link on every public page is the discoverable part. */}
          <li>
            <Link
              to={ROUTES.childSafety}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              Child Safety Standards
            </Link>
          </li>
          <li>
            <Link
              to={ROUTES.deleteAccount}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              Delete Your Account
            </Link>
          </li>
          {/* <li>
            <a
              href={`mailto:${CONTACT.grievanceEmail}`}
              className="font-body text-sm text-white/70 transition-colors hover:text-white"
            >
              Grievance Officer
            </a>
          </li> */}
        </ul>
      </nav>
    </div>

    <div className="border-t border-white/10">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-2 px-5 py-6 font-body text-xs text-white/40 md:flex-row md:items-center md:justify-between md:px-8">
        <p>
          © {year} {LEGAL_ENTITY.name}. All rights reserved.
        </p>
        <p>Built for campuses across India.</p>
      </div>
    </div>
  </footer>
);

export default SiteFooter;
